import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
export const meaningfulMinuteSchema = z.object({
    id: z.string(),
    name: z.string(),
    completedToday: z.boolean(),
});
export const dailyMeaningfulMinutesResponseSchema = z.object({
    meaningfulMinutes: z.array(meaningfulMinuteSchema),
});
/**
 * Query key for one day's selection. Shared by the query and the completion mutations,
 * which optimistically patch and then invalidate exactly this entry.
 */
export const getDailyMeaningfulMinutesQueryKey = (date) => ['meaningfulMinutes', 'daily', date];
/**
 * Returns a copy of a cached daily selection with one item's `completedToday` set, for
 * the completion mutations' optimistic update. Anything that isn't a successful response
 * with data is returned untouched, as is the response when the id isn't in it.
 */
export const setMeaningfulMinuteCompleted = (response, id, completedToday) => {
    if (!response?.data)
        return response;
    return {
        ...response,
        data: {
            ...response.data,
            meaningfulMinutes: response.data.meaningfulMinutes.map((meaningfulMinute) => meaningfulMinute.id === id ? { ...meaningfulMinute, completedToday } : meaningfulMinute),
        },
    };
};
/**
 * The day's Meaningful Minutes. A POST rather than a GET because the first call for a
 * date picks that day's selection server-side; later calls for the same date return the
 * same selection, so it is safe to refetch.
 */
export const useGetDailyMeaningfulMinutes = (date, authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: getDailyMeaningfulMinutesQueryKey(date),
        queryFn: () => authenticatedRequest({
            url: '/meaningful-minutes/daily-selection',
            method: 'POST',
            data: { date },
        }, dailyMeaningfulMinutesResponseSchema),
        enabled: autoFetch && !!date,
    });
};
/**
 * Shared body of the complete and undo mutations: identical apart from the request and
 * the `completedToday` value they optimistically write into the day's cached selection.
 */
const useMeaningfulMinuteCompletionMutation = (authenticatedRequest, completedToday) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, date }) => {
            // Both endpoints answer 204 with no body; the request function skips schema
            // validation for an empty body, so z.any() mirrors the habits DELETE hooks.
            return authenticatedRequest(completedToday
                ? { url: `/meaningful-minutes/${id}/completions`, method: 'POST', data: { date } }
                : { url: `/meaningful-minutes/${id}/completions/${date}`, method: 'DELETE' }, z.any());
        },
        onMutate: async ({ id, date }) => {
            const queryKey = getDailyMeaningfulMinutesQueryKey(date);
            // Stop an in-flight refetch from landing on top of the optimistic value.
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (current) => setMeaningfulMinuteCompleted(current, id, completedToday));
            return { previous };
        },
        onError: (error, { date }, context) => {
            if (context?.previous) {
                queryClient.setQueryData(getDailyMeaningfulMinutesQueryKey(date), context.previous);
            }
        },
        // Returned, not fired and forgotten, so a caller awaiting mutateAsync only clears its
        // pending state once the refetch has landed -- see useDeleteCompletion in habits.api.
        onSettled: (data, error, { date }) => queryClient.invalidateQueries({ queryKey: getDailyMeaningfulMinutesQueryKey(date) }),
    });
};
/** Marks a Meaningful Minute done for the given local date. */
export const useCompleteMeaningfulMinute = (authenticatedRequest) => useMeaningfulMinuteCompletionMutation(authenticatedRequest, true);
/** Undoes a Meaningful Minute's completion for the given local date. */
export const useUndoMeaningfulMinuteCompletion = (authenticatedRequest) => useMeaningfulMinuteCompletionMutation(authenticatedRequest, false);
